import { formatDODID, isValidDODID } from "@ths/shared/utils";
import type { FC, FormEvent } from "react";

import Spinner from "../components/Spinner";

interface EdipiStepProps {
  edipi: string;
  setEdipi: (value: string) => void;
  onSubmit: (e?: FormEvent) => void;
  isSubmitting: boolean;
  error: string;
  mode?: "checkin" | "checkout" | "login" | "request" | "reservation";
  onBack?: () => void;
}

const EdipiStep: FC<EdipiStepProps> = ({
  edipi,
  setEdipi,
  onSubmit,
  isSubmitting,
  error,
  mode = "checkin",
  onBack,
}) => (
  <div className="relative z-10 flex flex-1 flex-col items-center px-6">
    <form
      onSubmit={onSubmit}
      className="grid w-full max-w-sm flex-1 grid-rows-[1fr_auto_1fr]"
    >
      {/* Top row: content pinned to bottom */}
      <div className="flex flex-col justify-end pb-20">
        {/* Logo */}
        <div className="bc-fade-up text-center">
          <img
            src={new URL("../assets/ths-footer-logo.svg", import.meta.url).href}
            alt="Team Housing Solutions"
            className="mx-auto w-48 sm:w-56"
          />
        </div>

        {/* Divider */}
        <div className="bc-fade-up bc-fade-up-1 mt-10 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Heading + step */}
        <div className="bc-fade-up bc-fade-up-1 mt-10 text-center">
          <h1 className="font-oswald text-4xl font-medium uppercase tracking-wide text-white sm:text-5xl">
            {mode === "login"
              ? "Login"
              : mode === "checkout"
                ? "Check Out"
                : mode === "request"
                  ? "Report an Issue"
                  : mode === "reservation"
                    ? "Find Your Stay"
                    : "Check In"}
          </h1>
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/25">
            Basecamp Lodging
          </p>
        </div>
      </div>

      {/* Center row: input at exact vertical center */}
      <input
        id="basecamp-edipi"
        type="text"
        inputMode="text"
        pattern="\d{7,11}|[A-Za-z]{2,3}\d{4}"
        maxLength={11}
        autoComplete="off"
        value={edipi}
        onChange={(e) => {
          const val = formatDODID(e.target.value.replace(/[^A-Za-z0-9]/g, ""));
          if (val.length <= 11) setEdipi(val);
        }}
        placeholder="Enter your DOD ID"
        // text-base (16px) is the iOS Safari auto-zoom threshold; anything
        // smaller and Mobile Safari/WKWebView pinch-zooms the page on focus
        // to "help" the user, breaking the kiosk layout.
        className="bc-fade-up bc-fade-up-2 w-full rounded-lg border border-white/10 bg-white/[0.05] px-4 py-3.5 text-center text-base text-white outline-none transition-all placeholder:text-white/20 focus:border-accentGreen/40 focus:bg-white/[0.07] focus:ring-0"
      />

      {/* Bottom row: button pinned to top */}
      <div className="bc-fade-up bc-fade-up-2 pt-5">
        {error && (
          <div className="bc-fade-up mb-5 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-300">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!isValidDODID(edipi) || isSubmitting}
          className={`w-full rounded-xl px-8 py-4 font-oswald text-base font-medium uppercase tracking-wider transition-all active:scale-[0.98] ${
            isValidDODID(edipi) && !isSubmitting
              ? "bg-accentGreen text-primaryDarkBlue shadow-lg shadow-accentGreen/25"
              : "bg-accentGreen/20 text-accentGreen/40"
          }`}
        >
          {isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <Spinner />
              Looking up...
            </span>
          ) : (
            "Continue"
          )}
        </button>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="mt-3 w-full py-3 text-sm font-medium text-white/25 transition-colors hover:text-white/40"
          >
            Go Back
          </button>
        )}
      </div>
    </form>
  </div>
);

export default EdipiStep;
